import { ILogger } from '../../interfaces/ILogger';
import { AdapterContext } from '../../model/adapters/adapter-context';
import { ReferenceAdapter } from '../../model/adapters/reference.adapter';
import { Metamodel } from '../../model/base/model';
import { Owned } from '../../model/base/named';
import { Parameter } from '../../model/base/parameter';
import { ParameterValue } from '../../model/base/parameter-value';
import { ParameterizableNamed } from '../../model/base/parameterizable-named';
import { getIdentity } from '../../model/services/identifier';
import { SharedModel as Model } from '../../model/sharedmodel';

export type SupportedParameterTypes = string | number | boolean | Array<{}> | Model.SecureString;

export function convertParametersToModel(
    owner: Owned,
    replaceIdentity: boolean,
    reference: ParameterizableNamed,
    parameterValues: ParameterValue[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resourceParameters?: { [propertyName: string]: any },
    updateReferencedParameters: boolean = true): void {
    parameterValues.length = 0;
    if (resourceParameters) {
        Object.keys(resourceParameters).map((parameterIdentity) => {
            const parameterValue = Owned.create<ParameterValue>(ParameterValue.MODEL, owner);
            const attribute = ParameterValue.MODEL.attribute('value');

            if (resourceParameters[parameterIdentity] && resourceParameters[parameterIdentity].type === 'Expression') {
                attribute.setTemplateValue(parameterValue, resourceParameters[parameterIdentity].value);
            } else {
                attribute.setValue(parameterValue, stringifyParameterValue(resourceParameters[parameterIdentity]));
            }
            const parameterName = parameterIdentity;
            if (replaceIdentity) {
                parameterIdentity = AdapterContext.getIdentity(getIdentity(reference), parameterIdentity);
            }
            const parameter = ReferenceAdapter.toProxyReferenceModel<Parameter>(Metamodel.modelFor(Parameter), parameterIdentity);
            parameter.name = parameterName;
            parameterValue.parameter = parameter;
            if (updateReferencedParameters && reference.parameters) {
                reference.parameters.add(parameter);
            }
            parameterValues.push(parameterValue);
        });
    }
}

export function fillParameterValuesForResource(
    parameterList: ParameterValue[],
    adapterContext: AdapterContext,
    loggerService: ILogger,
    logEventFunc?: (invokedBy: string, loggerService: ILogger) => void
): { [propertyName: string]: {} } {
    if (!parameterList) {
        return {};
    }
    const parameters: { [propertyName: string]: {} } = {};
    parameterList.forEach((param) => {
        const attribute = Metamodel.modelOf(param).attribute('value');
        const value = attribute.templateValue(param);
        if (param.parameter) {
            if (value !== undefined) {
                parameters[adapterContext.identityProvider.identity(param.parameter)] = {
                    value,
                    type: 'Expression'
                };
            } else {
                parameters[adapterContext.identityProvider.identity(param.parameter)] = convertParameterValue(attribute.value(param), param.parameter._type,
                    'ParameterConverterUtil', loggerService, false, logEventFunc);
            }
        }
    });

    return parameters;
}

/**
 * Convert parameter value to be the actual type. If the value is invalid, we will keep the original string value.
 */
export function convertParameterValue(value: string, valueType: string, invokedBy: string, loggerService: ILogger,
    doNotParseValueIntoObjectWithNonObjectTypeSetting: boolean = false, logEventFunc: (invokedBy: string, loggerService: ILogger) => void): SupportedParameterTypes {
    if (value == null || value === '') {
        return undefined;
    }
    if (!valueType) {
        console.warn('Missing param type, for parameter value - ' + value);
        return value;
    }
    // TODO: Need to find a proper way to handle object type with secureString.
    // Previously, this function will parse value into an object by default and ignore value type setting.
    // We add flag doNotParseValueIntoObjectWithNonObjectTypeSetting to control whether we want this behavior or not
    // Default value for doNotParseValueIntoObjectWithNonObjectTypeSetting is false to maintain the existing behavior.
    // If the flag is set to true, we'll only convert value with object type into an object.
    // Say user input value is [1,2,3] and value type is string, if set this flag as false, the generated value is: [1,2,3], if true, generating "[1,2,3]".
    if (!doNotParseValueIntoObjectWithNonObjectTypeSetting || (valueType.toLowerCase() === 'object')) {
        try {
            const object = JSON.parse(value);
            if (typeof object === 'object') {
                if (valueType.toLowerCase() !== 'object' && logEventFunc) {
                    logEventFunc(invokedBy, loggerService);
                }
                return object;
            }
            // eslint-disable-next-line no-empty, @typescript-eslint/no-empty-function
        } catch { }
    }
    switch (valueType.toLowerCase()) {
        case 'string':
            break;
        case 'integer':
        case 'int':
            if (Number.isInteger(Number(value))) {
                return Number(value);
            }
            break;
        case 'float':
            if (!isNaN(parseFloat(value))) {
                return Number(value);
            }
            break;
        case 'boolean':
        case 'bool':
            if (String(value) === 'true' || String(value) === 'false') {
                return (String(value) === 'true') as boolean;
            }
            break;
        case 'array':
            try {
                const array = JSON.parse(String(value));
                if (Array.isArray(array)) {
                    return array;
                }
            } catch {
                break;
            }
            break;
        case 'securestring':
            const secureStringObj: Model.SecureString = {
                type: 'SecureString',
                value
            };
            return doNotParseValueIntoObjectWithNonObjectTypeSetting ? value : secureStringObj;
        default:
            console.warn('Unhandled param type, for parameter type - ' + valueType);
            break;
    }
    return value;
}

/**
 * stringify parameter value.
 */
export function stringifyParameterValue(value: SupportedParameterTypes): string {
    if (Array.isArray(value) || typeof value === 'object') {
        if (checkIsSecureString(value)) {
            const secureStringValue = value as Model.SecureString;
            return secureStringValue.value;
        }
        return JSON.stringify(value);
    } else {
        return value == null ? value as (null | undefined) : value.toString();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function checkIsSecureString(valueInQuestion: any): valueInQuestion is Model.SecureString {
        return valueInQuestion && valueInQuestion.type === 'SecureString';
    }
}
